import {
  getDomainFromSolution,
  MAIN_PRODUCT_CATEGORIES,
  PRODUCT_CATEGORIES_NL,
  Solution,
  SolutionDomain,
  SOLUTIONS_NL,
} from '@energiebespaarders/constants';
import { sum } from 'lodash';
import { useMemo } from 'react';
import { PriceUnit, ProductCategory } from '../types/graphql-global-types';
import delimit from '../utils/delimit';
import fixUnit from '../utils/fixUnit';
import { formatBrandName } from './../utils/formatBrandName';
import type { BrandedProduct } from '../envCustomer/MySingleQuote/components/ProductInfo';
import type {
  energyPassport_house_sentQuotes,
  energyPassport_house_sentQuotes_items,
} from '../types/generated/energyPassport';
import type {
  mySingleQuote_quoteById_items as gqlItem,
  mySingleQuote_quoteById_items_product_PvPanel as gqlPanel,
  mySingleQuote_quoteById_items_product_CompletePvSystem as gqlPvSystem,
  mySingleQuote_quoteById as gqlQuote,
  mySingleQuote_quoteById_items_product_Inverter,
} from '../types/generated/mySingleQuote';
import type {
  customerDashboardDeals_house_deals_sentQuotes,
  customerDashboardDeals_house_deals_sentQuotes_items,
} from './../types/generated/customerDashboardDeals';
import type { BrandName, ProductCategory as ProductCat } from '@energiebespaarders/constants';

// ! This one is also in humulus, keep in sync!

type QuoteInput =
  | gqlQuote
  | energyPassport_house_sentQuotes
  | customerDashboardDeals_house_deals_sentQuotes;

export function getPvProductsFromQuote(quote: QuoteInput) {
  const pvSystemItem = quote.items.find(
    i => i.product.category === ProductCategory.completePvSystem,
  );
  const pvSystemProduct = pvSystemItem?.product as gqlPvSystem | undefined;

  const pvPanelItems = quote.items.filter(i => i.product.category === ProductCategory.pvPanel);
  const pvPanelProducts = pvPanelItems.map(i => i.product as gqlPanel);

  const pvInverterItem = quote.items.find(i => i.product.category === ProductCategory.inverter);
  const pvInverterProduct = pvInverterItem?.product as
    | mySingleQuote_quoteById_items_product_Inverter
    | undefined;

  const pvOptimizerItem = quote.items.find(i => i.product.category === ProductCategory.optimizer);
  const pvLaborItem = quote.items.find(i => i.product.category === ProductCategory.labor);

  return {
    pvSystemItem,
    pvSystemProduct,
    pvPanelItems,
    pvPanelProducts,
    pvInverterItem,
    pvInverterProduct,
    pvOptimizerItem,
    pvLaborItem,
  };
}

export function formatPvQuoteTitleOutput(
  numPvPanels: number,
  panelBrand: string | undefined,
  pMax: number | undefined,
  inverterBrand: string,
  hasOptimizer: boolean,
  hasLabor: boolean,
) {
  return (
    `${numPvPanels} ${panelBrand ? `${panelBrand} ` : ''}panelen ${pMax ? `van ${pMax}Wp ` : ''}` +
    `inclusief ${inverterBrand} omvormer${
      hasOptimizer ? `${hasLabor ? ', ' : ' en '}optimizers` : ''
    }` +
    `${hasLabor ? ' en installatie' : ''}`
  );
}

/**
 * Formats the title of a PV quote
 * @param quote the quote
 * @param fullSentence whether to return a full sentence or separate parts for the main title and the subtitle, e.g.
 * "10 panelen van 300Wp inclusief omvormer en installatie" vs "10 panelen van 300Wp" and "inclusief omvormer en installatie"
 * @returns string with the title of the quote (or undefined if it's not a PV quote) or an array with the main title and the subtitle if fullSentence is false
 */
function formatPvQuoteTitle(quote: QuoteInput) {
  /* PV System */
  const {
    pvSystemItem,
    pvSystemProduct,
    pvPanelItems,
    pvPanelProducts,
    pvInverterItem,
    pvInverterProduct,
    pvOptimizerItem,
    pvLaborItem,
  } = getPvProductsFromQuote(quote);

  if (
    pvSystemItem &&
    pvSystemProduct?.panelAmount &&
    pvSystemProduct.pvPanel?.brand &&
    pvSystemProduct.pvPanel.pMax
  ) {
    const panelBrand = formatBrandName(pvSystemProduct.pvPanel.brand.name as BrandName);
    const inverterBrand = formatBrandName(pvSystemProduct.pvPanel.brand.name as BrandName);
    return (
      `${pvSystemProduct.panelAmount} ${panelBrand} panelen van ${pvSystemProduct.pvPanel?.pMax}Wp ` +
      `inclusief ${inverterBrand} omvormer${pvSystemProduct.optimizer ? ', optimizers' : ''} ` +
      'en installatie'
    );
  } else if (
    pvPanelItems.length &&
    pvInverterItem &&
    pvPanelProducts[0]?.brand &&
    pvPanelProducts[0].pMax &&
    pvInverterProduct?.brand
  ) {
    // There may be multiple panel products, of different WP, and different brand too (probably unlikely)
    const numPvPanels = sum(pvPanelItems.map(item => item.amount));
    const pMax = pvPanelProducts.every(p => p.pMax === pvPanelProducts[0].pMax)
      ? pvPanelProducts[0].pMax
      : undefined;
    const brand = pvPanelProducts.every(p => p.brand === pvPanelProducts[0].brand)
      ? pvPanelProducts[0].brand
      : undefined;
    return formatPvQuoteTitleOutput(
      numPvPanels,
      brand?.name,
      pMax,
      pvInverterProduct.brand.name,
      !!pvOptimizerItem,
      !!pvLaborItem,
    );
  }
}

function formatHeatingQuoteTitle(
  mainItem:
    | gqlItem
    | energyPassport_house_sentQuotes_items
    | customerDashboardDeals_house_deals_sentQuotes_items
    | undefined,
  useSimpleTitle?: boolean,
) {
  /* Heating */
  const heatingProduct = mainItem?.product as BrandedProduct;

  if (heatingProduct.title) {
    const productTitleIncludesBrand =
      heatingProduct.brand &&
      !heatingProduct.title.toLowerCase().includes(heatingProduct.brand.name.toLowerCase());

    if (useSimpleTitle && heatingProduct.brand) {
      return `${formatBrandName(heatingProduct.brand.name as BrandName)} ${
        PRODUCT_CATEGORIES_NL[heatingProduct.category]
      }`;
    }

    if (productTitleIncludesBrand) {
      return `${formatBrandName(heatingProduct.brand.name as BrandName)} ${heatingProduct.title}`;
    } else {
      return `${heatingProduct.title}`;
    }
  }
}

export function formatQuoteTitle(
  quote:
    | gqlQuote
    | energyPassport_house_sentQuotes
    | customerDashboardDeals_house_deals_sentQuotes
    | null
    | undefined,
  useSimpleTitle?: boolean,
): string {
  if (!quote) return '';
  const solution = quote.solution;
  const domain = getDomainFromSolution(solution);

  const mainItem = quote.items.filter(item =>
    MAIN_PRODUCT_CATEGORIES.includes(item.product.category as unknown as ProductCat),
  )[0] as customerDashboardDeals_house_deals_sentQuotes_items | undefined;

  if (domain === SolutionDomain.PvSystem) {
    const pvQuoteTitle = formatPvQuoteTitle(quote);
    if (pvQuoteTitle) return pvQuoteTitle;
  } else if (domain === SolutionDomain.Heating) {
    const heatingQuoteTitle = formatHeatingQuoteTitle(mainItem, useSimpleTitle);
    if (heatingQuoteTitle) return heatingQuoteTitle;
  } else {
    /* Other solution domains */
    if (mainItem) {
      const productTitle = mainItem.product.title || '';
      const hideAmount =
        (mainItem?.amount === 1 &&
          [
            Solution.CentralHeatingBoiler,
            Solution.ElectricHeatPump,
            Solution.HybridHeatPump,
            Solution.Miscellaneous,
            Solution.ChargingBox,
          ].some(solution => quote.solution === solution)) ||
        productTitle.includes('renovatie');

      const decimals =
        mainItem.amount.toString().split('.')?.[1] === '0'
          ? ''
          : mainItem.amount.toString().split('.')?.[1] ?? '';

      /** @returns " stuk", " stuks" or the actual unit */
      const formattedUnit: string =
        mainItem.product.priceUnit === PriceUnit.unit
          ? ` ${fixUnit(mainItem.product.priceUnit || '', true)}${mainItem.amount !== 1 ? 's' : ''}`
          : fixUnit(mainItem.product.priceUnit || '', true);
      return hideAmount
        ? productTitle
        : `${delimit(mainItem.amount, decimals.length)}${formattedUnit} ${productTitle}`;
    }
  }

  /* Ultimate fallback */
  console.error(`Using ultimate fallback for quote title with id ${quote.id}`);
  return `Offerte ${quote.reference} voor ${SOLUTIONS_NL[solution]}`; // Absolute default default fallback (really should not come to this)
}

export function useFormatQuoteTitle(
  quote: QuoteInput | null | undefined,
  useSimpleTitle?: boolean,
): string {
  return useMemo((): string => formatQuoteTitle(quote, useSimpleTitle), [quote, useSimpleTitle]);
}
